import { useEffect, useState } from 'react';
import { formatAmountInput, formatEuro, parseAmountInput } from '@brimo/shared';
import { Banner, Button, Dialog, TextField, Textarea } from '../../ds/components';
import { ApiError, errorMessage } from '../../lib/api';
import { useManualVolume } from './api';
import styles from './SelfDisclosurePage.module.css';

export interface ManualVolumeDialogProps {
  open: boolean;
  selfDisclosureId: string;
  computedCents: number;
  currentCents: number | null;
  currentReason: string | null;
  onClose: () => void;
  onSaved: () => void;
}

const AMOUNT_REQUIRED = 'Bitte geben Sie einen Betrag in Euro ein.';
const REASON_REQUIRED = 'Bitte geben Sie eine Begründung an.';

/** „Anpassen" of Selbstauskunft 10: the used volume below the computed one with a mandatory reason → `POST …/volume/manual`. */
export function ManualVolumeDialog({
  open,
  selfDisclosureId,
  computedCents,
  currentCents,
  currentReason,
  onClose,
  onSaved,
}: ManualVolumeDialogProps) {
  const manual = useManualVolume(selfDisclosureId);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<{ amount?: string; reason?: string }>({});

  useEffect(() => {
    if (!open) return;
    setAmount(currentCents === null ? '' : formatAmountInput(currentCents));
    setReason(currentReason ?? '');
    setErrors({});
  }, [open, currentCents, currentReason]);

  function submit() {
    const cents = parseAmountInput(amount);
    const next: typeof errors = {};
    if (cents === null || cents < 0) next.amount = AMOUNT_REQUIRED;
    if (reason.trim() === '') next.reason = REASON_REQUIRED;
    setErrors(next);
    if (cents === null || Object.keys(next).length > 0) return;
    manual.mutate({ usedVolumeCents: cents, reason: reason.trim() }, { onSuccess: onSaved });
  }

  const serverFields = manual.error instanceof ApiError ? manual.error.fields : {};
  const genericError =
    manual.isError && Object.keys(serverFields).length === 0 ? errorMessage(manual.error) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={560}
      eyebrow="Volumen anpassen"
      title="Verwendetes Volumen festlegen"
      subtitle={`Rechnerisch ${formatEuro(computedCents)}. Das verwendete Volumen darf das rechnerische nicht übersteigen; die Begründung erscheint im Verlauf.`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={manual.isPending}>
            Abbrechen
          </Button>
          <Button loading={manual.isPending} onClick={submit}>
            Volumen übernehmen
          </Button>
        </>
      }
    >
      <div className={styles.dialogStack}>
        {genericError ? <Banner tone="danger">{genericError}</Banner> : null}
        <TextField
          label="Verwendetes Volumen"
          required
          type="money"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          error={errors.amount ?? serverFields['usedVolumeCents']}
        />
        <Textarea
          label="Begründung"
          required
          rows={3}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          error={errors.reason ?? serverFields['reason']}
          hint="z. B. „Sparkasse rechnet nur mit 13 Gehältern ohne Nebentätigkeit.“"
        />
      </div>
    </Dialog>
  );
}
